import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  NotebookPen,
  Trash2,
  X,
  Plus,
  ScanText,
  Bot,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { notesApi } from "../lib/api";
import { toast } from "sonner";

const PROJECTS = [
  { id: "ezScribe", label: "ezScribe", icon: ScanText, tint: "text-emerald-300" },
  { id: "ezBuildabot", label: "ezBuildabot", icon: Bot, tint: "text-purple-300" },
  { id: "general", label: "General", icon: Sparkles, tint: "text-blue-300" },
];

export const NotesPanel = ({ open, onOpenChange, initialProject = "general", initialText = "", sourceSessionId = null }) => {
  const [tab, setTab] = useState(initialProject);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState(initialText);
  const [newProject, setNewProject] = useState(initialProject);

  useEffect(() => {
    if (open) {
      setNewText(initialText);
      setNewProject(initialProject);
      setTab(initialProject);
    }
  }, [open, initialText, initialProject]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notesApi.list();
      setNotes(res.notes || []);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const saveNew = async () => {
    const text = newText.trim();
    if (!text) return;
    try {
      const res = await notesApi.create({
        project: newProject,
        text,
        source_session_id: sourceSessionId,
      });
      setNotes((n) => [res.note, ...n]);
      setNewText("");
      setTab(newProject);
      toast.success(`Saved to ${newProject}`);
    } catch (e) {
      toast.error("Could not save note");
    }
  };

  const remove = async (id) => {
    try {
      await notesApi.remove(id);
      setNotes((n) => n.filter((x) => x.id !== id));
      toast.success("Note removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="notes-panel"
        className="glass-strong border-white/10 max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <NotebookPen className="h-5 w-5 text-blue-300" />
            Voice notes
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Drop reminders into ezScribe, ezBuildabot, or a general shelf.
          </DialogDescription>
        </DialogHeader>

        {/* Composer */}
        <div className="rounded-xl bg-black/30 border border-white/10 p-3 space-y-2">
          <Textarea
            data-testid="new-note-textarea"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            rows={3}
            placeholder="Type or paste a reminder…"
            className="bg-black/40 border-white/10 focus-visible:ring-blue-500/40 font-mono text-sm"
          />
          <div className="flex items-center gap-2">
            <Select value={newProject} onValueChange={setNewProject}>
              <SelectTrigger
                data-testid="new-note-project-select"
                className="w-44 bg-black/40 border-white/10"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {PROJECTS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <p.icon className={`h-3.5 w-3.5 ${p.tint}`} />
                      {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="save-note-btn"
              onClick={saveNew}
              disabled={!newText.trim()}
              className="ml-auto bg-blue-600 hover:bg-blue-500"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Save note
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList
            data-testid="notes-tabs"
            className="bg-black/30 border border-white/10"
          >
            {PROJECTS.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                data-testid={`notes-tab-${p.id}`}
                className="data-[state=active]:bg-blue-600/25 data-[state=active]:text-white"
              >
                <p.icon className={`h-3.5 w-3.5 mr-1.5 ${p.tint}`} />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {PROJECTS.map((p) => {
            const list = notes.filter((n) => n.project === p.id);
            return (
              <TabsContent
                key={p.id}
                value={p.id}
                className="mt-3 max-h-[45vh] overflow-y-auto pr-1 space-y-2"
              >
                {loading && (
                  <div className="text-sm text-white/50 py-3">Loading…</div>
                )}
                {!loading && list.length === 0 && (
                  <div className="text-sm text-white/40 py-6 text-center border border-dashed border-white/10 rounded-lg">
                    No {p.label} notes yet.
                  </div>
                )}
                <AnimatePresence initial={false}>
                  {list.map((n) => (
                    <motion.div
                      key={n.id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      data-testid={`note-${n.id}`}
                      className="group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2.5 flex items-start gap-2 transition-colors"
                    >
                      <p.icon
                        className={`h-3.5 w-3.5 shrink-0 mt-1 ${p.tint}`}
                      />
                      <div className="flex-1 text-sm text-white/85 leading-relaxed whitespace-pre-wrap">
                        {n.text}
                        <div className="text-[10px] uppercase tracking-widest text-white/30 mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        data-testid={`delete-note-${n.id}`}
                        onClick={() => remove(n.id)}
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md hover:bg-red-500/20 flex items-center justify-center transition-opacity"
                        aria-label="Delete note"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-400/80" />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </TabsContent>
            );
          })}
        </Tabs>

        <DialogFooter>
          <Button
            data-testid="notes-close-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4 mr-1.5" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Compact "Pin as note" button used in transcript. */
export const PinNoteButton = ({ text, sessionId, className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="pin-note-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`opacity-0 group-hover:opacity-100 h-6 w-6 rounded-md hover:bg-white/10 flex items-center justify-center transition-opacity ${className}`}
        aria-label="Pin as note"
        title="Save as note"
      >
        <NotebookPen className="h-3.5 w-3.5 text-blue-300" />
      </button>
      <NotesPanel
        open={open}
        onOpenChange={setOpen}
        initialText={text}
        initialProject="general"
        sourceSessionId={sessionId}
      />
    </>
  );
};
